import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Clock, Anchor, CalendarDays, Heart, ChevronRight, ArrowRight, Repeat, Map as MapIcon,
} from "lucide-react";
import { api, formatErr, fileUrl } from "@/lib/api";
import { getGuest } from "@/lib/guest";
import { useGuestSessionWatch } from "@/lib/useGuestSessionWatch";
import { useTrackView } from "@/lib/analyticsTracker";
import { Button } from "@/components/ui/button";
import GuestHeader from "@/components/GuestHeader";
import BlobImage from "@/components/BlobImage";
import JourneyTimeline from "@/components/JourneyTimeline";
import JourneyMapDialog from "@/components/JourneyMapDialog";
import { useBranding } from "@/lib/branding";

function GuestItineraryStrip() { return null; }
// Legacy — replaced by the unified <JourneyTimeline />. Kept as a no-op
// stub so any stale references are inert; safe to delete after one cycle.

// Elegant, minimal-hue palette. All accents come from the existing accent /
// primary variables so the page slots in with the rest of the guest side.

function formatDuration(mins) {
  if (!mins) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return "—"; }
}

function daysSince(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  return Math.max(0, Math.round((Date.now() - then) / 86_400_000));
}

export default function GuestActivity() {
  useGuestSessionWatch();
  useTrackView("journey");
  const { name: guestName, id: guestId, cabin } = getGuest();
  const [data, setData] = useState(null);
  const [itinerary, setItinerary] = useState({ visible: false, stops: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mapOpen, setMapOpen] = useState(false);
  const { journeyPortMapEnabled, journeyRouteMapEnabled } = useBranding();
  // Recomputes the "day X aboard / Y days ahead" copy every 15 min and
  // on tab-visibility so an iPad left on this page for the whole
  // charter never gets stuck on yesterday's numbers.
  const [tick, setTick] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!guestId) return;
    setLoading(true);
    // Fetch activity + intermediate itinerary stops in parallel so the
    // combined proportional timeline can render in one pass.
    Promise.all([
      api.get(`/public/my-activity?guest_id=${encodeURIComponent(guestId)}`),
      api.get(`/public/my-itinerary?guest_id=${encodeURIComponent(guestId)}`).catch(() => ({ data: { visible: false, stops: [] } })),
    ])
      .then(([act, itin]) => {
        setData(act.data);
        setItinerary({ visible: !!itin.data?.visible, stops: itin.data?.stops || [] });
      })
      .catch((e) => setErr(formatErr(e.response?.data?.detail) || "Could not load your journey."))
      .finally(() => setLoading(false));
  }, [guestId]);

  // Live ticker: 15 min interval + wake-from-sleep re-tick. Also runs on
  // day rollover — computing `Math.floor((now - embark) / 86.4M)`
  // client-side means we never depend on the server's snapshot value.
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 15 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === "visible") setTick((t) => t + 1); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  // Charter-day maths, recomputed on every `tick`. Falls back to the
  // server-provided snapshots when the embark/disembark ISOs are missing
  // (older charters, incomplete rosters).
  const charterDays = useMemo(() => {
    if (!data) return null;
    const now = Date.now();
    let daysAboard = data.days_aboard ?? null;
    let daysRemaining = data.days_remaining ?? null;
    let totalDays = null;
    if (data.embark_at) {
      const e = new Date(data.embark_at).getTime();
      if (!Number.isNaN(e)) daysAboard = Math.max(0, Math.floor((now - e) / 86_400_000));
    }
    if (data.disembark_at) {
      const d = new Date(data.disembark_at).getTime();
      if (!Number.isNaN(d)) daysRemaining = Math.max(0, Math.ceil((d - now) / 86_400_000));
    }
    if (data.embark_at && data.disembark_at) {
      const e = new Date(data.embark_at).getTime();
      const d = new Date(data.disembark_at).getTime();
      if (!Number.isNaN(e) && !Number.isNaN(d)) {
        totalDays = Math.max(1, Math.ceil((d - e) / 86_400_000));
      }
    }
    return { daysAboard, daysRemaining, totalDays };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [data, tick]);

  // Human sub-title: "Day 7 of 21 aboard · 14 days ahead" — falls back
  // to the softer copy when we don't have the full charter window yet.
  const journeyLine = useMemo(() => {
    if (!data) return "";
    if (!charterDays || charterDays.daysAboard == null) {
      // Legacy "since first spa visit" fallback for guests without a
      // charter window recorded on their roster row.
      if (data.total_sessions === 0) return "Your bespoke wellness journey begins here.";
      const days = daysSince(data.first_session_at);
      if (days === 0) return "Welcome back — your journey started today.";
      if (days === 1) return "One day into your journey aboard.";
      if (days < 7) return `${days} days into your journey aboard.`;
      if (days < 30) return `${Math.round(days / 7)} weeks into your journey aboard.`;
      return `Journeying with us since ${formatDate(data.first_session_at)}.`;
    }
    const { daysAboard, daysRemaining, totalDays } = charterDays;
    const dayN = daysAboard + 1;                     // "Day 1" on embark day, not "Day 0"
    const ofY = totalDays ? ` of ${totalDays}` : "";
    if (daysRemaining == null) return `Day ${dayN}${ofY} aboard.`;
    if (daysRemaining === 0) return `Day ${dayN}${ofY} · your final day aboard.`;
    if (daysRemaining === 1) return `Day ${dayN}${ofY} · one more day ahead.`;
    return `Day ${dayN}${ofY} · ${daysRemaining} more days ahead.`;
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [charterDays, data]);

  // Positive "how to enjoy them" prompt — scales the message to how
  // much of the voyage is left so it always feels contextual. Only
  // rendered when we actually have a charter window in hand.
  const spaPrompt = useMemo(() => {
    if (!charterDays || charterDays.daysRemaining == null) return "";
    const r = charterDays.daysRemaining;
    const a = charterDays.daysAboard ?? 0;
    if (a === 0 && r > 0) return "Welcome aboard — begin with a signature ritual to settle into the sea.";
    if (r === 0) return "Your final day — end it with a farewell treatment you'll carry home.";
    if (r === 1) return "One day left — the perfect window for a slow, restorative massage.";
    if (r <= 3) return `Just ${r} days to go — treat yourself before the horizon lets you go.`;
    if (r <= 7) return `${r} unhurried days ahead — the sea gives you time, the spa will use it beautifully.`;
    return `Plenty of time — ${r} open days ahead. Let a bespoke ritual set the pace of your voyage.`;
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [charterDays]);

  return (
    <div className="min-h-screen bg-background">
      <GuestHeader guestName={guestName} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-10 md:py-16">
        {loading ? (
          <div className="text-sm text-muted-foreground text-center py-24">Preparing your journey…</div>
        ) : err ? (
          <div className="text-sm text-destructive text-center py-24">{err}</div>
        ) : !data ? null : (
          <>
            {/* Hero */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mb-14 md:mb-20"
            >
              <div className="overline text-accent mb-4 flex items-center gap-2">
                <Anchor className="h-3 w-3" strokeWidth={2} />
                Private journey
              </div>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mb-3">
                {guestName || "Guest"},
                <span className="block text-accent italic font-light">your time aboard.</span>
              </h1>
              <p className="text-muted-foreground text-sm md:text-base max-w-xl">
                {journeyLine}
                {cabin && <> · <span className="text-foreground/70">{cabin}</span></>}
              </p>
              {spaPrompt && (
                <p className="text-accent italic text-sm md:text-base font-light max-w-xl mt-2" data-testid="journey-spa-prompt">
                  {spaPrompt}
                </p>
              )}

              {/* Unified proportional timeline — embark on the left, disembark
                  on the right, every intermediate port placed at (arrive_at −
                  embark_at) / voyage-span × 100%. Overlapping icons raise
                  on hover / touch so nothing gets hidden. */}
              {(data.embark_port || data.disembark_port || data.embark_at || data.disembark_at) && (
                <div className="mt-3 md:mt-4" data-testid="journey-band">
                  <JourneyTimeline
                    embarkPort={data.embark_port}
                    embarkAt={data.embark_at}
                    disembarkPort={data.disembark_port}
                    disembarkAt={data.disembark_at}
                    stops={itinerary.visible ? itinerary.stops : []}
                    daysRemaining={charterDays?.daysRemaining ?? data.days_remaining}
                    daysAboard={charterDays?.daysAboard ?? data.days_aboard}
                    portMapEnabled={journeyPortMapEnabled}
                  />
                  {/* Route map — full itinerary on a single Leaflet canvas
                      with soft sea-route curves between every stop. Only
                      surfaced when the guest actually has coordinates to
                      draw (any embark/disembark/stop with a name qualifies —
                      lat/lon are resolved on open) AND the Developer's
                      journey-route switch is enabled. */}
                  {journeyRouteMapEnabled && (data.embark_port || data.disembark_port || (itinerary.visible && itinerary.stops.length > 0)) && (
                    <div className="mt-6 flex justify-center">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setMapOpen(true)}
                        className="rounded-full border-[#d4af37]/60 hover:border-[#d4af37] text-[#c9992b] hover:text-[#c9992b] hover:bg-[#d4af37]/5 transition-colors"
                        data-testid="journey-open-map"
                      >
                        <MapIcon className="h-4 w-4 mr-2" />
                        View route on map
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </motion.div>

            {/* Stat row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border mb-14 md:mb-20" data-testid="activity-stats">
              <Stat label="Sessions enjoyed" value={data.total_sessions} testId="stat-sessions" />
              <Stat label="Total time" value={formatDuration(data.total_minutes)} testId="stat-time" />
              <Stat label="Still to come" value={data.upcoming || "—"} testId="stat-upcoming" />
              <Stat label="Last visit" value={data.total_sessions ? formatDate(data.last_session_at) : "—"} testId="stat-last" />
            </div>

            {/* Favourite prompt — only when count ≥ 5. Elegant, single-line CTA */}
            {data.favourite && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="mb-14 md:mb-16 border border-accent/30 bg-accent/5 p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5"
                data-testid="favourite-prompt"
              >
                <div className="flex-1 min-w-0">
                  <div className="overline text-accent mb-2 flex items-center gap-2">
                    <Heart className="h-3 w-3 fill-accent" strokeWidth={0} /> A ritual of yours
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl tracking-tight mb-1">
                    We suspect the <span className="italic text-accent">{data.favourite.treatment_name}</span> has become a favourite.
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    You've enjoyed it <span className="text-foreground">{data.favourite.count} times</span> aboard. Shall we prepare it again?
                  </p>
                </div>
                <Button
                  onClick={() => navigate(`/book/${data.favourite.treatment_id}`)}
                  className="rounded-full bg-accent hover:bg-accent/90 text-primary self-start md:self-auto shrink-0"
                  data-testid="favourite-book-btn"
                >
                  Reserve again <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </motion.div>
            )}

            {/* Treatments breakdown */}
            <section>
              <div className="flex items-baseline justify-between mb-6 md:mb-8 border-b border-border pb-3">
                <h2 className="font-display text-2xl md:text-3xl tracking-tight">Treatments you've enjoyed</h2>
                {data.by_treatment.length > 0 && (
                  <span className="overline text-primary/40 text-[10px]">
                    {data.by_treatment.length} {data.by_treatment.length === 1 ? "treatment" : "treatments"}
                  </span>
                )}
              </div>

              {data.by_treatment.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border">
                  <Sparkles className="h-6 w-6 mx-auto text-accent mb-3" strokeWidth={1.5} />
                  <p className="text-muted-foreground text-sm mb-5 max-w-md mx-auto">
                    Your journey hasn't begun yet. Explore the menu and let us curate something bespoke for you.
                  </p>
                  <Link to="/experience">
                    <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="empty-menu-btn">
                      Discover the menu <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-testid="treatment-breakdown">
                  {data.by_treatment.map((r, i) => (
                    <TreatmentRow key={r.treatment_id} row={r} index={i} onBook={() => navigate(`/book/${r.treatment_id}`)} />
                  ))}
                </div>
              )}
            </section>

            {/* Discreet closing gesture */}
            <div className="mt-16 md:mt-24 text-center overline text-primary/30 text-[10px]">
              — Your journey, kept in confidence —
            </div>
          </>
        )}
      </main>
      {/* Full-itinerary route popup — every stop pinned + soft sea-route
          curves between them. Mounted at the page root so its fixed
          overlay can layer above everything. Gated by the Developer
          switch so it never mounts (and its trigger button is hidden
          above) when route access is restricted. */}
      {journeyRouteMapEnabled && (
        <JourneyMapDialog
          open={mapOpen}
          onClose={() => setMapOpen(false)}
          embarkPort={data?.embark_port}
          embarkAt={data?.embark_at}
          disembarkPort={data?.disembark_port}
          disembarkAt={data?.disembark_at}
          stops={itinerary.visible ? itinerary.stops : []}
        />
      )}
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="bg-background p-5 md:p-6" data-testid={testId}>
      <div className="overline text-primary/50 text-[10px] mb-2">{label}</div>
      <div className="font-display text-2xl md:text-3xl tracking-tight leading-none">{value}</div>
    </div>
  );
}

function TreatmentRow({ row, index, onBook }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05 * index }}
      className="group border border-border hover:border-accent/50 transition-colors overflow-hidden bg-card flex"
      data-testid={`activity-treatment-${row.treatment_id}`}
    >
      {row.image_path ? (
        <div className="w-24 sm:w-32 shrink-0 bg-muted overflow-hidden">
          <BlobImage
            path={row.image_path}
            alt=""
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
          />
        </div>
      ) : (
        <div className="w-24 sm:w-32 shrink-0 bg-muted/60 flex items-center justify-center">
          <Sparkles className="h-6 w-6 text-accent/50" strokeWidth={1.2} />
        </div>
      )}
      <div className="flex-1 min-w-0 p-4 sm:p-5 flex flex-col">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="font-display text-lg sm:text-xl truncate">{row.treatment_name}</h3>
          <div
            className={`shrink-0 font-display text-lg sm:text-xl leading-none ${row.is_favourite ? "text-accent" : "text-foreground/60"}`}
            title={row.is_favourite ? "A favourite of yours" : ""}
            data-testid={`activity-count-${row.treatment_id}`}
          >
            ×{row.count}
          </div>
        </div>
        {row.is_favourite && (
          <div className="overline text-accent text-[10px] mb-2 inline-flex items-center gap-1">
            <Heart className="h-2.5 w-2.5 fill-accent" strokeWidth={0} /> favourite
          </div>
        )}
        <div className="text-xs text-muted-foreground space-y-0.5 mb-4">
          <div className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" /> {formatDuration(row.total_minutes)} in total</div>
          <div className="inline-flex items-center gap-1.5"><CalendarDays className="h-3 w-3" /> last visited {formatDate(row.last_at)}</div>
        </div>
        <div className="mt-auto">
          <button
            onClick={onBook}
            className="inline-flex items-center gap-1.5 text-sm text-accent hover:text-primary transition-colors"
            data-testid={`activity-book-${row.treatment_id}`}
          >
            <Repeat className="h-3.5 w-3.5" /> Reserve again
          </button>
        </div>
      </div>
    </motion.article>
  );
}
